import React from 'react';
import { trackWhatsAppClick } from '../utils/tracking';
import { toWhatsAppPhone } from '../utils/whatsappPhone';
import { withOriginCode } from '../utils/siteEvents';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiMapPin, FiArrowRight, FiHome } from 'react-icons/fi';
import { LuBed, LuBath, LuRuler, LuCar } from 'react-icons/lu';
import { Property } from '../types';
import { buildImageUrl } from '../services/api';
import { translatePropertyType } from '../utils/translations';
import { getUnavailability } from '../utils/availability';
import { isMixed } from '../utils/transactionType';
import WhatsAppIcon from './icons/WhatsAppIcon';
import { Badge, Stat } from './ui';

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ theme }) => theme.colors.card};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.card};
  overflow: hidden;
  color: inherit;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.hover};
    border-color: ${({ theme }) => theme.colors.neutral[300]};
  }
`;

const Media = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.neutral[100]};
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;

  ${Card}:hover & {
    transform: scale(1.06);
  }
`;

const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.neutral[100]},
    ${({ theme }) => theme.colors.neutral[200]}
  );
  color: ${({ theme }) => theme.colors.neutral[400]};
`;

const TopBadges = styled.div`
  position: absolute;
  top: 0.85rem;
  left: 0.85rem;
  right: 0.85rem;
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  z-index: 2;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(15, 23, 42, 0.72) 0%,
    rgba(15, 23, 42, 0.25) 32%,
    rgba(15, 23, 42, 0) 55%
  );
  z-index: 1;
`;

const PriceWrap = styled.div`
  position: absolute;
  left: 1rem;
  right: 1rem;
  bottom: 0.9rem;
  z-index: 2;
  color: #fff;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.45rem 1.15rem;
`;

const PriceItem = styled.div`
  min-width: 0;
`;

const PriceLabel = styled.span`
  display: block;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  opacity: 0.85;
`;

const Price = styled.span`
  display: block;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: clamp(1.05rem, 2vw, 1.35rem);
  font-weight: 700;
  line-height: 1.1;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 1.1rem 1.15rem 1.25rem;
`;

const Title = styled.h3`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 1.075rem;
  font-weight: 600;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.foreground};
  margin-bottom: 0.4rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.9em;
`;

const Location = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.875rem;
  margin-bottom: 1rem;

  svg {
    color: ${({ theme }) => theme.colors.neutral[400]};
    flex-shrink: 0;
  }
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Specs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1.1rem;
  padding-bottom: 1rem;
  margin-top: auto;
`;

const Foot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.9rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ViewLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  font-size: 0.9rem;

  svg {
    transition: transform 0.2s ease;
  }
  ${Card}:hover & svg {
    transform: translateX(3px);
  }
`;

const WppButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: #25d366;
  color: #fff;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: #1ebe5b;
    transform: translateY(-1px);
  }
`;

interface PropertyCardProps {
  property: Property;
}

const formatPrice = (price: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);

export interface DisplayPricing {
  business_type: 'SALE' | 'RENTAL';
  price: number | null;
}

export const getDisplayPricing = (
  property: Pick<Property, 'pricing_infos' | 'transaction_type' | 'price' | 'show_price'>
): DisplayPricing[] => {
  const explicitPrices = new Map<DisplayPricing['business_type'], number>();

  for (const pricing of property.pricing_infos || []) {
    const rawBusinessType = String(pricing.business_type || '').toUpperCase();
    const businessType = rawBusinessType === 'RENT' || rawBusinessType === 'RENTAL'
      ? 'RENTAL'
      : rawBusinessType === 'SALE'
        ? 'SALE'
        : null;
    const price = Number(pricing.price);
    if (businessType && Number.isFinite(price) && price > 0) {
      explicitPrices.set(businessType, price);
    }
  }

  const mixed = isMixed(property.transaction_type)
    || (explicitPrices.has('SALE') && explicitPrices.has('RENTAL'));
  const legacyPrice = Number(property.price);
  const safeLegacyPrice = Number.isFinite(legacyPrice) && legacyPrice > 0 ? legacyPrice : null;
  const visiblePrice = (price: number | null) => property.show_price === false ? null : price;
  const result: DisplayPricing[] = [];

  if (explicitPrices.has('SALE')) {
    result.push({ business_type: 'SALE', price: visiblePrice(explicitPrices.get('SALE')!) });
  } else if (mixed) {
    result.push({ business_type: 'SALE', price: visiblePrice(safeLegacyPrice) });
  }

  if (explicitPrices.has('RENTAL')) {
    result.push({ business_type: 'RENTAL', price: visiblePrice(explicitPrices.get('RENTAL')!) });
  } else if (mixed) {
    result.push({ business_type: 'RENTAL', price: null });
  }

  if (result.length > 0) return result;
  return [{
    business_type: property.transaction_type === 'aluguel' ? 'RENTAL' : 'SALE',
    price: visiblePrice(safeLegacyPrice),
  }];
};

const PropertyCard: React.FC<PropertyCardProps> = ({ property }) => {
  const imageUrl =
    property.cover_image ||
    (property.images && property.images.length > 0
      ? typeof property.images[0] === 'string'
        ? (property.images[0] as string)
        : (property.images[0] as any).url
      : null);

  const pricingInfos = getDisplayPricing(property);

  // Vale tanto o campo atual (commercial_status) quanto o legado (status).
  const unavailability = getUnavailability(property);
  const statusBadge = unavailability
    ? {
        label: unavailability.label,
        variant: unavailability.kind === 'sold' ? ('danger' as const) : ('neutral' as const),
      }
    : null;

  const location =
    property.location ||
    [property.city, property.state].filter(Boolean).join(', ') ||
    'Localização sob consulta';

  const brokerPhone = toWhatsAppPhone(property.managed_by?.phone || property.captured_by?.phone);

  return (
    <Card to={`/imovel/${property.id}`}>
      <Media>
        {imageUrl ? (
          <Img src={buildImageUrl(imageUrl)} alt={property.title} loading="lazy" />
        ) : (
          <Placeholder>
            <FiHome size={44} />
          </Placeholder>
        )}

        <TopBadges>
          <span>
            {property.is_featured && <Badge $variant="glass">Destaque</Badge>}
          </span>
          <span>{statusBadge && <Badge $variant={statusBadge.variant}>{statusBadge.label}</Badge>}</span>
        </TopBadges>

        <Overlay />
        <PriceWrap>
          {pricingInfos.map(item => (
            <PriceItem key={item.business_type}>
              <PriceLabel>{item.business_type === 'RENTAL' ? 'Locação / mês' : 'Venda'}</PriceLabel>
              <Price>{item.price === null ? 'Sob consulta' : formatPrice(item.price)}</Price>
            </PriceItem>
          ))}
        </PriceWrap>
      </Media>

      <Body>
        <Title>{property.title}</Title>
        <Location>
          <FiMapPin size={15} />
          <span>{location}</span>
        </Location>

        <Specs>
          {!!property.bedrooms && (
            <Stat>
              <LuBed size={17} /> {property.bedrooms}{' '}
              {property.bedrooms === 1 ? 'quarto' : 'quartos'}
            </Stat>
          )}
          {!!property.bathrooms && (
            <Stat>
              <LuBath size={17} /> {property.bathrooms}{' '}
              {property.bathrooms === 1 ? 'banh.' : 'banhs.'}
            </Stat>
          )}
          {!!property.area && (
            <Stat>
              <LuRuler size={17} /> {property.area} m²
            </Stat>
          )}
          {!!property.garage_spaces && property.garage_spaces > 0 && (
            <Stat>
              <LuCar size={17} /> {property.garage_spaces}{' '}
              {property.garage_spaces === 1 ? 'vaga' : 'vagas'}
            </Stat>
          )}
          {!property.bedrooms && !property.bathrooms && !property.area && property.type && (
            <Stat>
              <FiHome size={16} /> {translatePropertyType(property.type)}
            </Stat>
          )}
        </Specs>

        <Foot>
          <ViewLink>
            Ver detalhes <FiArrowRight size={16} />
          </ViewLink>
          {brokerPhone && (
            <WppButton
              href={`https://wa.me/${brokerPhone}?text=${encodeURIComponent(
                withOriginCode(`Olá, vi o imóvel "${property.title}" no site e gostaria de mais informações.`)
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                trackWhatsAppClick('property_card');
              }}
              aria-label="Conversar no WhatsApp"
            >
              <WhatsAppIcon size={18} color="white" />
            </WppButton>
          )}
        </Foot>
      </Body>
    </Card>
  );
};

export default PropertyCard;
